import React from 'react'
import { ItemData } from '../../core/models/Template'
import { IActionToPermissionMap } from '../../organisms/TreeViewCatalog/TreeViewCatalogList/TreeViewCatalogList-types'

export interface CatalogItemView {
    selected?: boolean
    isOpen?: boolean
    index?: number
    nestedKey?: string
}

export interface CatalogItem {
    id: number
    type?: string
    __view?: CatalogItemView
    children?: CatalogItem[]
}

export interface CatalogListItem extends CatalogItem {
    __view: CatalogItemView & {
        selected: boolean
        isOpen: boolean
        index: number
    }
    children?: CatalogListItem[]
}

export type CatalogDraft = Record<string, unknown>

export interface CatalogField {
    id: string
    name: React.ReactNode
    hide?: boolean
    useRawData?: boolean
}

export interface CatalogCustomDataField extends CatalogField {}

export interface CatalogCustomDataItem {
    id: number
    fields: Array<{
        id: string
        value: React.ReactNode
    }>
}

export interface CatalogCustomData {
    fields: CatalogCustomDataField[]
    alterFieldValue: Record<string, React.ReactNode>
    exceptionParams: Array<{
        name: string
        value: unknown
    }>
    dataList: CatalogCustomDataItem[]
}

export type CatalogCellRenderer = (
    item: CatalogItem | CatalogDraft,
    active: boolean,
    props: ICatalogListProps,
    nestedLevel?: number
) => React.ReactNode

type CatalogEntityHandler = {
    bivarianceHack(item: CatalogItem): void
}['bivarianceHack']

type CatalogEqualityHandler = {
    bivarianceHack(firstItem: { id: number }, secondItem: { id: number }): boolean
}['bivarianceHack']

type CatalogPredicate = {
    bivarianceHack(item: { id: number }): boolean
}['bivarianceHack']

type CatalogSelectionHandler = {
    bivarianceHack(data: { selected: boolean; item: CatalogItem }): void
}['bivarianceHack']

export interface CatalogListTemplate {
    getItemClassName?: (item: CatalogItem) => string
    list?: Record<string, CatalogCellRenderer>
    isFolder: CatalogPredicate
    fixedColWidth?: boolean
    templateCellsWidth?: Record<string, string> | React.CSSProperties[]
    tableWrapper?: string
    onDeleteListItem?: CatalogEntityHandler
    trNoClick?: boolean
    multipleMove?: (sourceIds: number[], folderIds: number[], targetId: number) => Promise<unknown>
    isEqual: CatalogEqualityHandler
    moveFolder: (id: number, targetId: number) => unknown
    moveItem: (id: number, targetId: number) => unknown
    customElementInSelect?: (item: CatalogItem, active: boolean) => React.ReactNode
    hideActiveRowColor?: boolean
    dndDblClickDisable?: boolean
}

export interface CatalogListItemData {
    listItem: CatalogListItem
    index: number
    folderKey?: string
    options: CatalogField[]
    isFolder: 'folder' | 'item'
}

export interface ICatalogListProps {
    options: CatalogField[] | null
    customData?: CatalogCustomData
    hideSelection?: boolean
    template: CatalogListTemplate
    isFolder: CatalogPredicate
    multipleSelect: boolean
    treeView?: boolean
    allowEmptyList?: boolean
    fakeLine: CatalogDraft | null
    list: CatalogItem[]
    getDragData?: (params: { item: CatalogItem }) => unknown
    onSelectInfo: (index: number) => void
    selectedItems: ItemData[]
    onResetSelection: () => void
    selectedInfo?: ItemData
    isEqual?: CatalogEqualityHandler
    drag?: boolean
    onDragStart?: (event: React.DragEvent) => void
    onDragEnd?: (event: React.DragEvent) => void
    onSelectFolder?: (data: { id: number }) => void
    onDoubleClickObject?: CatalogEntityHandler
    disableFolderSelection?: boolean
    disableMultiSelect: CatalogPredicate
    onSelect: CatalogSelectionHandler
    onFolderOpening?: (listItem: CatalogItem, afterCreateAction?: boolean) => void
    nested?: number
    nestedLevel?: number
    onEditListItem?: CatalogEntityHandler
    onSortEnd?: ({ oldIndex, newIndex }: { oldIndex: number; newIndex: number }) => void
    type: string
    onAddFakeLine: (fakeLine: CatalogDraft | null) => void
    additionalData?: unknown
    handleSortable?: boolean
    identificatedItems?: Array<{ id: number; timeoutS: number }>
    selectAllCheckbox?: boolean
    onSelectAll?: () => void
    onCancelSelectAll?: () => void
    setTableRef?: (ref: HTMLTableElement) => void
    onChangePermissions?: (permissions: IActionToPermissionMap) => void
    permissions?: IActionToPermissionMap
    disableSticky?: boolean
}

export type CatalogCellValueGetter = (
    listItem: CatalogItem | CatalogDraft,
    id: string,
    active: boolean,
    nestedLevel?: number
) => React.ReactNode

export const getCatalogValue = (item: CatalogItem | CatalogDraft, key: string): unknown => {
    return (item as Record<string, unknown>)[key]
}

export const isCatalogItem = (value: unknown): value is CatalogItem => {
    if (typeof value !== 'object' || value === null) {
        return false
    }

    return typeof (value as { id?: unknown }).id === 'number'
}

export const toCatalogCellNode = (value: unknown): React.ReactNode => {
    if (
        value === null ||
        value === undefined ||
        typeof value === 'string' ||
        typeof value === 'number' ||
        typeof value === 'boolean'
    ) {
        return value
    }

    return React.isValidElement(value as {} | null | undefined) ? (value as React.ReactElement) : null
}
